import { fetchUtils } from "react-admin";
import { hydraDataProvider } from "@api-platform/admin";
import { parseHydraDocumentation } from '@api-platform/api-doc-parser';

const entrypoint = process.env.REACT_APP_API_ENTRYPOINT;

const fetchHeaders = () => {
    const token = localStorage.getItem("token");
    const tokenType = localStorage.getItem("tokenType") || "Bearer";
    return token ? { Authorization: `${tokenType} ${token}` } : {};
};

const customParseHydraDocumentation = (entrypointUrl, options = {}) => {
    const customHeaders = fetchHeaders();
    options.headers = {
        ...options.headers,
        ...customHeaders,
    };
    return parseHydraDocumentation(entrypointUrl, options);
};

const httpClient = (url, options = {}) => {
    options.headers = new Headers({
        Accept: "application/ld+json",
        ...fetchHeaders(),
    });
    return fetchUtils.fetchJson(url, options);
};

/**
 * Lazily creates the Hydra data provider so it is always initialized
 * AFTER the user has logged in and a valid token is in localStorage.
 * Reset _dataProvider to null on logout to force a fresh fetch next login.
 */
let _dataProvider = null;

export const resetDataProvider = () => {
    _dataProvider = null;
};

const getBaseProvider = () => {
    if (!_dataProvider) {
        _dataProvider = hydraDataProvider({
            entrypoint,
            httpClient,
            apiDocumentationParser: customParseHydraDocumentation,
        });
    }
    return _dataProvider;
};

/**
 * Extracts the numeric / slug ID from an IRI.
 *
 * Handles both forms that API Platform may return depending on environment:
 *   - Relative path : /api/courses/1
 *   - Absolute URL  : https://staging-api.yanfaa.ai/api/courses/1
 */
const extractId = (iri, resource) => {
    if (!iri) return iri;

    let path = iri;

    // Strip the origin if this is a full absolute URL
    try {
        const url = new URL(iri);
        path = url.pathname; // → /api/courses/1
    } catch (_) {
        // Not a full URL — treat as a relative path already
    }

    const prefix = `/api/${resource}/`;
    if (path.startsWith(prefix)) {
        return path.substring(prefix.length);
    }
    return path;
};

/**
 * Builds a relative IRI from a bare ID.
 * Guards against double-wrapping when the ID is already an IRI or full URL.
 */
const buildIri = (id, resource) => {
    const str = String(id);
    // Already a full URL or relative IRI — return as-is
    if (str.startsWith('http') || str.startsWith('/')) return str;
    return `/api/${resource}/${str}`;
};

const customDataProvider = new Proxy({}, {
    get(_, prop) {
        const target = getBaseProvider();
        if (typeof target[prop] === 'function') {
            return function(resource, params) {
                let modifiedParams = { ...params };

                if (prop === 'getList') {
                    if (modifiedParams.sort.field === 'id')
                        modifiedParams.sort = { field: 'createdAt', order: 'DESC' };
                    const { field, order } = modifiedParams.sort;
                    modifiedParams.sort = {
                        field,
                        order: order.toLowerCase()
                    };
                }

                if (['getOne', 'update', 'delete'].includes(prop)) {
                    modifiedParams = { ...modifiedParams, id: buildIri(modifiedParams.id, resource) };
                } else if (prop === 'getMany') {
                    modifiedParams = { ...modifiedParams, ids: modifiedParams.ids.map(id => buildIri(id, resource)) };
                }

                return target[prop](resource, modifiedParams).then(response => {
                    if (response.data) {
                        if (Array.isArray(response.data)) {
                            response.data = response.data.map(record => {
                                const id = extractId(record.id, resource);
                                return { ...record, id };
                            });
                        } else if (response.data.id) {
                            const id = extractId(response.data.id, resource);
                            response.data = { ...response.data, id };
                        }
                    }
                    return response;
                });
            };
        }
        return target[prop];
    }
});

export default customDataProvider;
